import { ParseTemplateRequestDto } from '@asset-metrix/portal-gateway-client-js';
import { isObject } from '@asset-metrix/portal-ui';

import { extractErrorMessage } from '../../utils/errorMessage';
import { HttpError } from '../errors';
import { portalGatewayClient } from '../portalGatewayClient';

export type ParseTemplateValidationError = {
  message: string;
  row?: number;
  column?: string;
};

export type ParseTemplateResult =
  | { status: 'success'; data: Record<string, unknown> }
  | {
      status: 'fail';
      message: string;
      errors?: ParseTemplateValidationError[];
    };

function extractValidationErrors(
  error: unknown
): ParseTemplateValidationError[] | undefined {
  if (
    isObject(error) &&
    isObject(error.data) &&
    Array.isArray(error.data.errors) &&
    error.data.errors.length > 0
  ) {
    return error.data.errors;
  }
  return undefined;
}

function extractDataMessage(error: unknown): string | undefined {
  if (
    isObject(error) &&
    isObject(error.data) &&
    typeof error.data.message === 'string'
  ) {
    return error.data.message;
  }
  return undefined;
}

export const parseDataIngestionTemplate = async (
  contentId: string,
  data: ParseTemplateRequestDto
): Promise<ParseTemplateResult> => {
  const response = await portalGatewayClient.dataIngestion.parseTemplate(
    contentId,
    data
  );

  if (!response.ok) {
    let error = response.error;
    try {
      error = await response.json();
    } catch {
      // ignore - use error property instead
    }

    const validationErrors = extractValidationErrors(error);
    if (validationErrors !== undefined) {
      return {
        status: 'fail',
        message: extractErrorMessage(error, 'Template validation failed.'),
        errors: validationErrors,
      };
    }

    const dataMessage = extractDataMessage(error);
    if (dataMessage !== undefined) {
      return {
        status: 'fail',
        message: dataMessage,
      };
    }

    throw new HttpError(
      extractErrorMessage(error, 'Error loading data ingestion entities.'),
      response
    );
  }

  return response.data;
};
